// Sarge's Counter (IMPLEMENTATION §2.6 "Chrome contents", UX §2 Counter / §4): Sarge (hit.sargeW wide, idle
// bob + blink from the art/domIcons Sarge frames; still in ?test), the SELL zone (flex; a DOM drop zone —
// its rect is provided to the input's DropZoneRegistry) and the round Merge All button (hit.mergeAll, with
// its cooldown ring; from u13). Tapping anything but Merge All opens the Shop — or, while something is
// selected, sells it (M6 `router.tapZone('counter')`). Sarge's lines (the cold-open script, tutorial tips,
// hints) show as his speech bubble inside the counter, where "here" in "Drag here to sell" points.
import { useEffect, useState } from 'preact/hooks';
import type { CmdResult } from '../../sim/commands.ts';
import { hasFeature, mergeAllCooldown } from '../../sim/query.ts';
import { fillCopy } from '../copy.ts';
import { shellCtx } from '../ctx.ts';
import { money } from '../format.ts';
import { draggingSig, hoverZoneSig, layoutSig, sargeSig, selectionSig } from '../state.ts';
import { snapshotSig } from '../store.ts';
import { box } from './box.ts';
import { Icon } from './Icon.tsx';

type Pose = 'idle' | 'bob' | 'blink';

function Sarge({ px }: { px: number }) {
  const ctx = shellCtx();
  const cfg = ctx.data().ui.sarge;
  const [pose, setPose] = useState<Pose>('idle');
  useEffect(() => {
    if (ctx.instant) return; // ?test: no idle animation (§2.9 test mode)
    // pose from the wall clock: bob every sarge.bobMs / 2, a blink of blinkMs every blinkEveryMs
    const id = setInterval(() => {
      const now = performance.now();
      if (now % cfg.blinkEveryMs < cfg.blinkMs) setPose('blink');
      else setPose(Math.floor(now / (cfg.bobMs / 2)) % 2 === 1 ? 'bob' : 'idle');
    }, cfg.blinkMs);
    return () => clearInterval(id);
  }, [ctx.instant, cfg.bobMs, cfg.blinkEveryMs, cfg.blinkMs]);
  return (
    <span class="sarge" data-testid="sarge" data-pose={pose}>
      <Icon frame={`sarge.${pose}`} px={px} />
    </span>
  );
}

function MergeAll({ px }: { px: number }) {
  const ctx = shellCtx();
  void snapshotSig.value; // 10 Hz: the cooldown ring follows the sim clock
  const s = ctx.state();
  const d = ctx.data().sim;
  const left = mergeAllCooldown(s);
  const total = Math.max(1, d.economy.mergeAllCooldownTicks);
  const pct = Math.round((100 * left) / total);
  const copy = ctx.data().copy;
  const onClick = (e: MouseEvent): void => {
    const r: CmdResult = ctx.actions.dispatch({ c: 'mergeAll' });
    if (!r.ok && r.reason !== 'cooldown') {
      const b = (e.currentTarget as HTMLElement).getBoundingClientRect();
      ctx.actions.refuse(r.reason, { x: b.x + b.width / 2, y: b.y });
    }
  };
  return (
    <button
      type="button"
      class={`merge-all ctl ${left > 0 ? 'cooling' : ''}`}
      style={{ '--cd': `${pct}%`, width: `${px}px`, height: `${px}px` }}
      data-hit="merge-all"
      data-testid="merge-all"
      data-fx="mergeAll"
      aria-label={copy.counter.mergeAll ?? ''}
      onClick={onClick}
    >
      <Icon frame="ui.mergeAll" px={ctx.data().ui.counter.mergeAllIconPx} />
    </button>
  );
}

export function CounterBar() {
  const l = layoutSig.value;
  void snapshotSig.value; // re-read the unlocks (counter / Merge All / Shop) at the snapshot rate
  if (!l) return null;
  const ctx = shellCtx();
  const f = ctx.shell.fmt();
  const s = ctx.state();
  const d = ctx.data().sim;
  const hit = ctx.data().ui.hit;
  const hot = hoverZoneSig.value === 'counter';
  const dragging = draggingSig.value;
  const sel = selectionSig.value;
  const line = sargeSig.value;
  const selling = hasFeature(s, d, 'counter');
  const shop = hasFeature(s, d, 'shop');
  const c = f.copy.counter;
  let zone: { text: string; bubble: boolean } | null = null;
  if (dragging) zone = selling ? { text: c.sell ?? '', bubble: false } : null;
  else if (sel?.k === 'item' && sel.sellValue !== null)
    zone = {
      text: fillCopy(c.tapToSell ?? '{value}', { value: money(f, sel.sellValue) }),
      bubble: false,
    };
  else if (line !== null) zone = { text: line.text, bubble: true };
  else if (selling) zone = { text: c.sell ?? '', bubble: false };
  const onClick = (): void => {
    if (ctx.actions.selected()) ctx.actions.tapZone('counter');
    else if (shop) {
      ctx.actions.click();
      ctx.shell.togglePanel('shop');
    }
  };
  return (
    <div
      class={`counter ${hot ? 'hot' : ''} ${selling ? '' : 'locked'}`}
      style={box(l.counter)}
      data-zone="counter"
      data-fx="counter"
      data-testid="counter"
    >
      <button
        type="button"
        class="counter-main ctl"
        data-hit="counter"
        aria-label={shop ? (f.copy.tabs.shop ?? '') : (c.sell ?? '')}
        onClick={onClick}
      >
        <Sarge px={Math.min(hit.sargeW, l.counter.h - ctx.data().ui.counter.inset)} />
        <span class="sell-zone">
          {zone === null ? null : zone.bubble ? (
            <span class="sarge-bubble" data-testid="sarge-line" data-key={line?.key ?? ''}>
              {zone.text}
            </span>
          ) : (
            <span class="sell-text" data-testid="sell-text">
              {zone.text}
            </span>
          )}
        </span>
      </button>
      {hasFeature(s, d, 'mergeAll') ? (
        <MergeAll px={Math.min(hit.mergeAll, l.counter.h - 2 * ctx.data().ui.counter.inset)} />
      ) : null}
    </div>
  );
}
